import { useMemo, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { InlineEditShell } from "./InlineEditShell";
import { useInlineEdit } from "./useInlineEdit";
import { sameDay, dateKey } from "@/lib/inline-date";


export default function InlineEditDate({
  value,
  onCommit,
  canEdit,
  label = "previsão",
  children,
  className,
  disabledReason,
}: {
  value: Date | string | null;
  onCommit: (next: Date | null) => Promise<void>;
  canEdit: boolean;
  label?: string;
  children: React.ReactNode;
  className?: string;
  disabledReason?: string;
}) {
  // Memoizado por timestamp: sem isso, cada render produzia um Date novo e o
  // hook via "valor mudou" para sempre.
  const key = dateKey(value);
  const current = useMemo(() => (key == null ? null : new Date(key)), [key]);
  const [open, setOpen] = useState(false);

  const edit = useInlineEdit<Date | null>({
    value: current,
    onCommit,
    disabled: !canEdit,
    isEqual: sameDay,
  });

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        if (!canEdit) return;
        setOpen(v);
        if (v) edit.start();
        else edit.cancel();
      }}
    >
      {/* O botão é o gatilho do Radix; a casca só desenha. Ver `interactive`
          em InlineEditShell. */}
      <PopoverTrigger asChild disabled={!canEdit}>
        <button
          type="button"
          aria-label={`Editar ${label}`}
          className="max-w-full text-left disabled:cursor-default"
          disabled={!canEdit}
          onClick={(e) => e.stopPropagation()}
        >
          <InlineEditShell
            canEdit={canEdit}
            status={edit.status}
            error={edit.error}
            label={label}
            triggerProps={{}}
            interactive={false}
            className={className}
            disabledReason={disabledReason}
          >
            {children}
          </InlineEditShell>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <Calendar
          mode="single"
          selected={edit.draft ?? undefined}
          onSelect={(d) => {
            setOpen(false);
            void edit.commit(d ?? null);
          }}
          initialFocus
        />
        {current && (
          <>
            <Separator />
            <div className="p-1">
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-7 text-xs"
                onClick={() => {
                  setOpen(false);
                  void edit.commit(null);
                }}
              >
                Limpar previsão
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
